/**
 * Utility functions for handling authentication tokens and secure storage
 */

import { resetUnauthorizedFlag } from "@config/axios/axios.config";
import type { User } from "@features/Login/types";
import { clearUser, initializeUser } from "@store/slices/userSlice";
import { store } from "@store/store";

export const AUTH_STORAGE_USER_KEY = "user";
export const AUTH_STORAGE_TOKEN_KEY = "token";

const USER_KEY = AUTH_STORAGE_USER_KEY;
const TOKEN_KEY = AUTH_STORAGE_TOKEN_KEY;

// Helper to get the appropriate storage based on rememberMe preference
const getStorage = (usePersistentStorage: boolean = true): Storage => {
	return usePersistentStorage ? localStorage : sessionStorage;
};

export const getAuthToken = (): string | null => {
	try {
		// Check localStorage first (for "Remember Me" users)
		const persistentToken = localStorage.getItem(TOKEN_KEY);
		if (persistentToken) {
			return persistentToken;
		}
		// Fallback to sessionStorage (for session-only users)
		return sessionStorage.getItem(TOKEN_KEY);
	} catch (error) {
		console.error("An error occured while getting token:", error);
		return null;
	}
};

export const setAuthToken = (token: string, rememberMe: boolean = true): void => {
	try {
		const storage = getStorage(rememberMe);
		// Clear from the other storage to avoid conflicts
		if (rememberMe) {
			sessionStorage.removeItem(TOKEN_KEY);
		} else {
			localStorage.removeItem(TOKEN_KEY);
		}
		storage.setItem(TOKEN_KEY, token);
	} catch (error) {
		console.error("An error occured while setting token:", error);
	}
};

export const removeAuthToken = (): void => {
	try {
		localStorage.removeItem(TOKEN_KEY);
		sessionStorage.removeItem(TOKEN_KEY);
		store.dispatch(clearUser());
	} catch (error) {
		console.error("An error occured while removing token:", error);
	}
};

export const getUser = (): User | null => {
	try {
		// Check localStorage first (for "Remember Me" users)
		const persistentUser = localStorage.getItem(USER_KEY);
		if (persistentUser) {
			return JSON.parse(persistentUser);
		}
		// Fallback to sessionStorage (for session-only users)
		const sessionUser = sessionStorage.getItem(USER_KEY);
		return sessionUser ? JSON.parse(sessionUser) : null;
	} catch (error) {
		console.error("An error occured while getting user:", error);
		return null;
	}
};

export const setUser = (user: User, rememberMe: boolean = true): void => {
	try {
		const storage = getStorage(rememberMe);
		// Clear from the other storage to avoid conflicts
		if (rememberMe) {
			sessionStorage.removeItem(USER_KEY);
		} else {
			localStorage.removeItem(USER_KEY);
		}
		storage.setItem(USER_KEY, JSON.stringify(user));
	} catch (error) {
		console.error("An error occured while setting user:", error);
	}
};

export const removeUser = (): void => {
	try {
		localStorage.removeItem(USER_KEY);
		sessionStorage.removeItem(USER_KEY);
	} catch (error) {
		console.error("An error occured while removing user:", error);
	}
};

export const clearAuthData = (): void => {
	removeAuthToken();
	removeUser();
	sessionStorage.clear();

	// Clear auth-related cookies
	document.cookie.split(";").forEach(cookie => {
		const [name] = cookie.trim().split("=");
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	});
};

export const initializeAuthState = (): void => {
	resetUnauthorizedFlag();
	const user = getUser();
	const token = getAuthToken();
	store.dispatch(initializeUser({ user, token }));
};
